// ============================================================
// Ebek Sales CRM — Map View + Visit Route Optimiser
// Uses Leaflet.js via CDN (no npm install needed)
// ============================================================
import React, { useEffect, useRef, useState, useCallback } from "react";
import toast from "react-hot-toast";
import API from "../utils/api";

// Temperature / stage colour config
const TEMP_COLOR = {
  hot:  { fill: "#dc2626", label: "🔥 Hot" },
  warm: { fill: "#f59e0b", label: "🌤 Warm" },
  cold: { fill: "#3b82f6", label: "❄️ Cold" },
};

const STAGE_SHORT = {
  visited:           "V",
  interested:        "I",
  proposal_sent:     "P",
  mou_shared:        "M",
  mou_signed:        "MS",
  payment_requested: "PR",
  closed_won:        "W",
  closed_lost:       "L",
};

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Nearest-neighbour greedy route from a starting point
function buildRoute(startLat, startLon, leads) {
  const remaining = [...leads];
  const route = [];
  let curLat = startLat;
  let curLon = startLon;

  while (remaining.length > 0) {
    let nearest = null;
    let nearestDist = Infinity;
    let nearestIdx = -1;

    remaining.forEach((l, i) => {
      const d = haversineKm(curLat, curLon, parseFloat(l.latitude), parseFloat(l.longitude));
      if (d < nearestDist) { nearestDist = d; nearest = l; nearestIdx = i; }
    });

    if (nearest) {
      route.push({ ...nearest, dist_km: nearestDist.toFixed(1) });
      curLat = parseFloat(nearest.latitude);
      curLon = parseFloat(nearest.longitude);
      remaining.splice(nearestIdx, 1);
    }
  }
  return route;
}

export default function MapView({ accent }) {
  const mapRef       = useRef(null);
  const leafletMap   = useRef(null);
  const markersLayer = useRef(null);
  const routeLayer   = useRef(null);

  const [leads, setLeads]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [userPos, setUserPos]     = useState(null);
  const [route, setRoute]         = useState([]);
  const [routeBuilt, setRouteBuilt] = useState(false);
  const [filterTemp, setFilterTemp] = useState("all"); // all | hot | warm | cold
  const [leafletReady, setLeafletReady] = useState(false);
  const [selected, setSelected]   = useState(null);
  const [routeDestination, setRouteDestination] = useState("");

  // ── Load Leaflet CSS + JS from CDN ──────────────────────────
  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }

    const link = document.createElement("link");
    link.rel  = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => setLeafletReady(true);
    document.head.appendChild(script);

    return () => {
      // Don't remove — other components may reuse it
    };
  }, []);

  // ── Fetch lead institutions with coordinates ─────────────────
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get("/leads/institutions");
      // Only keep leads that have GPS from a past visit
      setLeads(r.data.filter(l => l.latitude && l.longitude));
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // ── Initialise map once Leaflet is ready ─────────────────────
  useEffect(() => {
    if (!leafletReady || !mapRef.current || leafletMap.current) return;
    const L = window.L;

    leafletMap.current = L.map(mapRef.current, { zoomControl: true }).setView([20.5937, 78.9629], 6);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(leafletMap.current);

    markersLayer.current = L.layerGroup().addTo(leafletMap.current);
    routeLayer.current   = L.layerGroup().addTo(leafletMap.current);
  }, [leafletReady]);

  // ── Render markers whenever leads or filter change ───────────
  useEffect(() => {
    if (!leafletReady || !leafletMap.current || !markersLayer.current) return;
    const L = window.L;
    markersLayer.current.clearLayers();

    const visible = filterTemp === "all"
      ? leads
      : leads.filter(l => l.lead_status === filterTemp);

    if (visible.length === 0) return;

    const bounds = [];

    visible.forEach(lead => {
      const lat  = parseFloat(lead.latitude);
      const lon  = parseFloat(lead.longitude);
      const tc   = TEMP_COLOR[lead.lead_status] || TEMP_COLOR.warm;
      const stg  = STAGE_SHORT[lead.pipeline_stage] || "?";

      // Custom circle marker with stage label
      const icon = L.divIcon({
        className: "",
        html: `<div style="
          width:32px; height:32px; border-radius:50%;
          background:${tc.fill}; border:3px solid #fff;
          box-shadow:0 2px 6px rgba(0,0,0,0.35);
          display:flex; align-items:center; justify-content:center;
          color:#fff; font-size:10px; font-weight:800;
          font-family:system-ui,sans-serif;
        ">${stg}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -18],
      });

      const marker = L.marker([lat, lon], { icon });

      const popupHtml = `
        <div style="font-family:system-ui,sans-serif; min-width:180px">
          <div style="font-weight:800; font-size:13px; color:#1e293b; margin-bottom:4px">${lead.institution_name}</div>
          <div style="font-size:11px; color:#64748b">${lead.city || ""} ${lead.institution_type || ""}</div>
          <div style="margin-top:6px; display:flex; gap:6px; flex-wrap:wrap">
            <span style="background:${tc.fill}20; color:${tc.fill}; border-radius:4px; padding:2px 7px; font-size:10px; font-weight:700">${tc.label}</span>
            <span style="background:#f1f5f9; color:#475569; border-radius:4px; padding:2px 7px; font-size:10px; font-weight:700">${lead.pipeline_stage?.replace(/_/g, " ") || ""}</span>
          </div>
          ${lead.contact_name ? `<div style="font-size:11px; color:#64748b; margin-top:5px">👤 ${lead.contact_name}</div>` : ""}
          ${lead.contact_phone ? `<div style="font-size:11px; color:#64748b">📞 ${lead.contact_phone}</div>` : ""}
          ${lead.approx_revenue ? `<div style="font-size:11px; color:#15803d; margin-top:4px; font-weight:700">Est. ₹${Number(lead.approx_revenue).toLocaleString("en-IN")}</div>` : ""}
        </div>
      `;

      marker.bindPopup(popupHtml, { maxWidth: 240 });
      marker.on("click", () => setSelected(lead));
      markersLayer.current.addLayer(marker);
      bounds.push([lat, lon]);
    });

    if (bounds.length > 0) {
      leafletMap.current.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [leads, filterTemp, leafletReady]);

  // ── Draw route polyline ──────────────────────────────────────
  useEffect(() => {
    if (!leafletReady || !routeLayer.current) return;
    const L = window.L;
    routeLayer.current.clearLayers();

    if (route.length < 2 || !userPos) return;

    const coords = [
      [userPos.lat, userPos.lon],
      ...route.map(l => [parseFloat(l.latitude), parseFloat(l.longitude)]),
    ];

    L.polyline(coords, { color: accent, weight: 3, dashArray: "8 6", opacity: 0.8 })
      .addTo(routeLayer.current);

    // Number the stops
    route.forEach((l, i) => {
      const icon = window.L.divIcon({
        className: "",
        html: `<div style="
          width:22px; height:22px; border-radius:50%;
          background:${accent}; border:2px solid #fff;
          display:flex; align-items:center; justify-content:center;
          color:#fff; font-size:10px; font-weight:800;
          font-family:system-ui,sans-serif; box-shadow:0 1px 4px rgba(0,0,0,0.3);
        ">${i + 1}</div>`,
        iconSize: [22, 22], iconAnchor: [11, 11],
      });
      L.marker([parseFloat(l.latitude), parseFloat(l.longitude)], { icon })
        .addTo(routeLayer.current);
    });
  }, [route, userPos, accent, leafletReady]);

  // ── Build "Plan My Day" route ────────────────────────────────
  function handlePlanDay() {
    setRouteBuilt(false);
    setRoute([]);

    navigator.geolocation.getCurrentPosition(
      pos => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        setUserPos({ lat, lon });

        // Add user marker
        if (leafletReady && leafletMap.current) {
          const L = window.L;
          const icon = L.divIcon({
            className: "",
            html: `<div style="
              width:28px; height:28px; border-radius:50%;
              background:#1e293b; border:3px solid #fff;
              display:flex; align-items:center; justify-content:center;
              font-size:14px; box-shadow:0 2px 8px rgba(0,0,0,0.4);
            ">📍</div>`,
            iconSize: [28, 28], iconAnchor: [14, 14],
          });
          L.marker([lat, lon], { icon }).addTo(routeLayer.current)
            .bindPopup("<b>You are here</b>").openPopup();
        }

        const visible = filterTemp === "all"
          ? leads
          : leads.filter(l => l.lead_status === filterTemp);

        // Filter hot leads first, then warm for route optimisation
        const hotFirst = [
          ...visible.filter(l => l.lead_status === "hot"),
          ...visible.filter(l => l.lead_status === "warm"),
          ...visible.filter(l => l.lead_status === "cold"),
        ];

        const optimised = buildRoute(lat, lon, hotFirst);
        setRoute(optimised);
        setRouteBuilt(true);
        toast.success(`Route planned — ${optimised.length} stops`);
      },
      () => toast.error("Could not get your location. Please enable GPS."),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  function openGoogleMapsRoute(destination) {
    const dest = String(destination || "").trim();
    if (!dest) return toast.error("Enter an institution or location first");
    const encodedDest = encodeURIComponent(dest);
    if (userPos) {
      window.open(`https://www.google.com/maps/dir/${userPos.lat},${userPos.lon}/${encodedDest}`, "_blank", "noopener,noreferrer");
      return;
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          setUserPos({ lat, lon });
          window.open(`https://www.google.com/maps/dir/${lat},${lon}/${encodedDest}`, "_blank", "noopener,noreferrer");
        },
        () => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedDest}`, "_blank", "noopener,noreferrer"),
        { enableHighAccuracy: true, timeout: 8000 }
      );
      return;
    }
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedDest}`, "_blank", "noopener,noreferrer");
  }

  const visibleLeads = filterTemp === "all" ? leads : leads.filter(l => l.lead_status === filterTemp);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b" }}>🗺 Map View</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>
            {loading ? "Loading…" : `${visibleLeads.length} leads with GPS data`}
          </div>
        </div>
        <button onClick={handlePlanDay}
          style={{ padding: "10px 16px", background: accent, color: "#fff", border: "none",
            borderRadius: 12, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          🧭 Plan My Day
        </button>
      </div>

      {/* Filter chips */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {[
          { key: "all",  label: "All", color: "#1e293b", bg: "#f1f5f9" },
          { key: "hot",  label: "🔥 Hot",  color: "#dc2626", bg: "#fee2e2" },
          { key: "warm", label: "🌤 Warm", color: "#b45309", bg: "#fef3c7" },
          { key: "cold", label: "❄️ Cold", color: "#1d4ed8", bg: "#dbeafe" },
        ].map(f => (
          <button key={f.key} onClick={() => setFilterTemp(f.key)}
            style={{ padding: "6px 14px", borderRadius: 100, border: "none", fontWeight: 700,
              fontSize: 12, cursor: "pointer", fontFamily: "inherit",
              background: filterTemp === f.key ? f.color : "#f1f5f9",
              color: filterTemp === f.key ? "#fff" : "#64748b" }}>
            {f.label}
          </button>
        ))}
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 10,
        marginBottom: 12,
        padding: 12,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1.5px solid #e2e8f0"
      }}>
        <input
          value={routeDestination}
          onChange={e => setRouteDestination(e.target.value)}
          onKeyDown={e => e.key === "Enter" && openGoogleMapsRoute(routeDestination)}
          placeholder="Enter institution or address for Google Maps route"
          style={{
            minWidth: 0,
            border: "1px solid #cbd5e1",
            borderRadius: 10,
            padding: "10px 12px",
            fontFamily: "inherit",
            fontSize: 13,
            color: "#1e293b"
          }}
        />
        <button
          type="button"
          onClick={() => openGoogleMapsRoute(routeDestination)}
          style={{
            border: "none",
            borderRadius: 10,
            padding: "0 14px",
            background: "#1e293b",
            color: "#fff",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 800,
            cursor: "pointer"
          }}
        >
          Open Route
        </button>
      </div>

      {/* Map container */}
      <div style={{ flex: 1, borderRadius: 16, overflow: "hidden", border: "1.5px solid #e2e8f0",
        minHeight: 340, position: "relative", background: "#f8fafc" }}>
        {(!leafletReady || loading) && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center",
            justifyContent: "center", flexDirection: "column", gap: 12, zIndex: 10, background: "#f8fafc" }}>
            <div style={{ fontSize: 36 }}>🗺</div>
            <div style={{ fontWeight: 600, color: "#64748b" }}>Loading map…</div>
          </div>
        )}
        <div ref={mapRef} style={{ width: "100%", height: "100%", minHeight: 340 }} />
      </div>

      {/* Route plan list */}
      {routeBuilt && route.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#1e293b", marginBottom: 10 }}>
            🧭 Optimised Route — {route.length} stops
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {route.map((l, i) => {
              const tc = TEMP_COLOR[l.lead_status] || TEMP_COLOR.warm;
              return (
                <div key={l.lead_id || i}
                  style={{ display: "flex", alignItems: "center", gap: 12, background: "#fff",
                    border: "1.5px solid #f1f5f9", borderRadius: 12, padding: "10px 14px" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: accent,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontWeight: 800, fontSize: 12, flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {l.institution_name}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      {l.city || ""} · {l.dist_km} km from prev stop
                    </div>
                  </div>
                  <span style={{ background: tc.fill + "20", color: tc.fill,
                    borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                    {tc.label}
                  </span>
                </div>
              );
            })}
          </div>
          {/* Google Maps link */}
          {userPos && route.length > 0 && (
            <a
              href={`https://www.google.com/maps/dir/${userPos.lat},${userPos.lon}/${route.map(l => `${l.latitude},${l.longitude}`).join("/")}`}
              target="_blank" rel="noopener noreferrer"
              style={{ display: "block", marginTop: 12, textAlign: "center", padding: "12px",
                background: "#1e293b", color: "#fff", borderRadius: 12,
                fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
              📱 Open Full Route in Google Maps
            </a>
          )}
        </div>
      )}

      {selected && (
        <div style={{
          marginTop: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          background: "#fff",
          border: "1.5px solid #e2e8f0",
          borderRadius: 14,
          padding: "10px 12px"
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selected.institution_name}
            </div>
            <div style={{ fontSize: 11, color: "#64748b" }}>Selected institution route</div>
          </div>
          <button
            type="button"
            onClick={() => openGoogleMapsRoute(`${selected.latitude},${selected.longitude}`)}
            style={{
              border: "none",
              borderRadius: 10,
              padding: "10px 12px",
              background: accent,
              color: "#fff",
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
              flexShrink: 0
            }}
          >
            Route
          </button>
        </div>
      )}

      {/* No GPS data state */}
      {!loading && leads.length === 0 && (
        <div style={{ textAlign: "center", padding: 32, color: "#94a3b8", marginTop: 12 }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📍</div>
          <div style={{ fontWeight: 600 }}>No GPS data yet</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            GPS coordinates are recorded when you log visits from the field
          </div>
        </div>
      )}
    </div>
  );
}
